import { useQuery, useInfiniteQuery, type QueryClient } from '@tanstack/react-query'
import { v2, v2Unscoped, v2api, V2ApiError } from './v2'
import { usePOSStore } from '@/stores/posStore'
import type { Product, ProductCategory, ProductFilters, ProductLot, PaginatedResponse, UIProductFilters } from '@/types'


// Pricing-affecting query key segment. Including customer_id in every
// product query key ensures React Query refetches with the customer's
// tier-adjusted prices when the cashier picks a customer, instead of
// returning the cached shelf-price response.
//
// Only the cashier-selected customer counts. We deliberately do NOT fall
// back to `cart.preview.customer_id` (the retail default the BE assigns
// when no customer is picked, e.g. 3332): in this deployment the retail
// default's tier pricing is identical to no-customer pricing, so keying
// off the preview customer would just refetch products on the first cart
// add for no behavior change. If a deployment ever surfaces tier
// differences between "no customer" and the retail default, restore the
// fallback to keep product-modal prices in sync with cart-preview totals.
function usePricingKey() {
  const selectedCustomer = usePOSStore((s) => s.selectedCustomer)
  return { customer_id: selectedCustomer?.id ?? null }
}


export const productKeys = {
  all: ['products'] as const,
  lists: () => [...productKeys.all, 'list'] as const,
  list: (filters: ProductFilters, customerId: number | null) => [...productKeys.all, 'list', filters, customerId] as const,
  search: (query: string, customerId: number | null) => [...productKeys.all, 'search', query, customerId] as const,
  detail: (id: number) => [...productKeys.all, 'detail', id] as const,
  categories: () => [...productKeys.all, 'categories'] as const,
  // v2 marker bumps the cache key after the return shape changed from
  // `string[]` (just names) to `Brand[]` (id + name). The IDB-persisted
  // cache (lib/persister.ts) would otherwise restore the old shape and
  // the 1-hour staleTime would suppress a refetch, leaving the filter
  // modal rendering empty chips.
  brands: () => [...productKeys.all, 'brands', 'v2'] as const,
  types: () => [...productKeys.all, 'types'] as const,
  units: () => [...productKeys.all, 'units'] as const,
  topSellers: (days: number, limit: number, customerId: number | null) => [...productKeys.all, 'top-sellers', days, limit, customerId] as const,
  barcode: (barcode: string, customerId: number | null) => [...productKeys.all, 'barcode', barcode, customerId] as const,
}


interface ProductsSearchResponse {
  products: Product[]
  total: number
  page: number
  per_page: number
}



function uiFiltersToParams(uiFilters?: UIProductFilters): Record<string, unknown> {
  if (!uiFilters) return {}

  const params: Record<string, unknown> = {}


  if (uiFilters.productTypes.length > 0) {
    // Plural list-filter on the v2 search handler — applied as
    // `SA_Product.product_type_id IN (...)`. Mirrors brands /
    // category_groups / strains. Replaces the legacy
    // indica/sativa/hybrid=true booleans, which the v2 handler silently
    // ignored (they were never read), letting selections fall through
    // to an unfiltered result set.
    params.product_types = uiFilters.productTypes
  }

  if (uiFilters.units.length > 0) {
    // ID-based UOM multi-select on the v2 search handler. Distinct from
    // the legacy name-based `grams` filter set below — we send both when
    // both are active and let the BE AND them.
    params.units = uiFilters.units
  }


  if (uiFilters.thcRange[0] > 0) {
    params.thc_low = uiFilters.thcRange[0]
  }
  if (uiFilters.thcRange[1] < 100) {
    params.thc_high = uiFilters.thcRange[1]
  }


  if (uiFilters.cbdRange[0] > 0) {
    params.cbd_low = uiFilters.cbdRange[0]
  }
  if (uiFilters.cbdRange[1] < 100) {
    params.cbd_high = uiFilters.cbdRange[1]
  }


  if (uiFilters.brands.length > 0) {
    // BE param is `brands` (plural) and expects a list of brand IDs;
    // axios serializes the array as `?brands=42&brands=87` thanks to
    // the `indexes: null` paramsSerializer on apiClient.
    params.brands = uiFilters.brands
  }

  if (uiFilters.priceRanges.length > 0) {
    let minPrice: number | undefined
    let maxPrice: number | undefined

    for (const range of uiFilters.priceRanges) {
      if (range === 'under20') {
        minPrice = 0
        maxPrice = maxPrice !== undefined ? Math.max(maxPrice, 19.99) : 19.99
      } else if (range === '20to40') {
        minPrice = minPrice !== undefined ? Math.min(minPrice, 20) : 20
        maxPrice = maxPrice !== undefined ? Math.max(maxPrice, 39.99) : 39.99
      } else if (range === '40to80') {
        minPrice = minPrice !== undefined ? Math.min(minPrice, 40) : 40
        maxPrice = maxPrice !== undefined ? Math.max(maxPrice, 79.99) : 79.99
      } else if (range === 'over80') {
        minPrice = minPrice !== undefined ? Math.min(minPrice, 80) : 80
        maxPrice = undefined // No upper limit
      }
    }

    if (minPrice !== undefined && minPrice > 0) params.price_low = minPrice
    if (maxPrice !== undefined) params.price_high = maxPrice
  }


  if (uiFilters.weights.length > 0) {
    params.grams = uiFilters.weights.join(',')
  }

  // Multi-select category groups override the single-select bar setting.
  // Param is `category_groups` (plural) — `get_arg_list`-style. The
  // singular `category_group` (used by the categories bar below) is
  // `get_arg_int` and only accepts a single integer; sending
  // "1,2,3" silently fails int() parsing on the BE.
  if (uiFilters.categoryGroups && uiFilters.categoryGroups.length > 0) {
    params.category_groups = uiFilters.categoryGroups
  }

  return params
}


function buildProductParams(filters: ProductFilters & { uiFilters?: UIProductFilters }, page?: number, _perPage = 50): Record<string, unknown> {
  const params: Record<string, unknown> = {}

  // BE doesn't read `per_page` / `page_size` — page size is server-set
  // via the `po.product_search_limit` SysConfig (defaults to 100). Only
  // `page` is honored. The _perPage arg is kept so callers can still
  // shape their local response envelope, but never goes on the wire.
  if (page) {
    params.page = page
  }


  params.q = filters.search || ''

  if (filters.category_group_id && filters.category_group_id > 0) {
    params.category_group = filters.category_group_id
  }

  if (filters.category_group_id === -1) {
    params.onsale = true
  } else {
    params.onsale = false
  }

  if (filters.category_id && filters.category_id > 0) {
    params.category_group = filters.category_id
  }
  if (filters.subcategory_id) params.category = filters.subcategory_id
  if (filters.strain_type) params.strains = filters.strain_type
  if (filters.min_thc !== undefined) params.min_thc = filters.min_thc
  if (filters.max_thc !== undefined) params.max_thc = filters.max_thc
  if (filters.min_cbd !== undefined) params.min_cbd = filters.min_cbd
  if (filters.max_cbd !== undefined) params.max_cbd = filters.max_cbd
  if (filters.min_price !== undefined) params.price_low = filters.min_price
  if (filters.max_price !== undefined) params.price_high = filters.max_price
  if (filters.in_stock_only) params.in_stock = true

  const uiParams = uiFiltersToParams(filters.uiFilters)
  Object.assign(params, uiParams)

  return params
}


export function useProducts(filters: ProductFilters = {}, enabled = true) {
  const { customer_id } = usePricingKey()
  return useQuery({
    queryKey: productKeys.list(filters, customer_id),
    queryFn: async (): Promise<Product[]> => {
      const params = buildProductParams(filters)
      if (customer_id) params.customer_id = customer_id

      const data = await v2api.get<ProductsSearchResponse | Product[]>(v2('/products/search'), params)
      if (Array.isArray(data)) return data
      return data?.products ?? []
    },
    enabled,
    staleTime: 1000 * 30, // fast-changing POS data
    gcTime: 1000 * 60 * 10,
    networkMode: 'online',
  })
}


export function useInfiniteProducts(filters: ProductFilters & { uiFilters?: UIProductFilters } = {}, perPage = 50) {
  const { customer_id } = usePricingKey()
  return useInfiniteQuery({
    queryKey: [...productKeys.list(filters, customer_id), 'infinite', filters.uiFilters] as const,
    queryFn: async ({ pageParam = 1 }): Promise<PaginatedResponse<Product>> => {
      const params = buildProductParams(filters, pageParam, perPage)
      if (customer_id) params.customer_id = customer_id

      const data = await v2api.get<ProductsSearchResponse | Product[]>(v2('/products/search'), params)

      // BE returns the envelope shape ({products,total,page,per_page}); the
      // array branch is a defensive fallback for stale BE deploys and the
      // legacy v1 endpoint. With a known total we can stop paging exactly
      // when we've seen everything — the previous version had to chase a
      // trailing empty page, which is what made every search fire a
      // phantom page 2.
      if (Array.isArray(data)) {
        return {
          items: data,
          total: data.length,
          page: pageParam,
          per_page: perPage,
          total_pages: data.length < perPage ? pageParam : pageParam + 1,
        }
      }

      const respPerPage = data.per_page || perPage
      const respTotal = data.total || 0
      return {
        items: data.products || [],
        total: respTotal,
        page: data.page || pageParam,
        per_page: respPerPage,
        total_pages: respTotal > 0 ? Math.ceil(respTotal / respPerPage) : 1,
      }
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.items.length === 0) return undefined
      if (lastPage.page >= lastPage.total_pages) return undefined
      return lastPage.page + 1
    },
    staleTime: 1000 * 30,
    gcTime: 1000 * 60 * 10,
    networkMode: 'online',
  })
}


export function useProductSearch(query: string, enabled = true) {
  const { customer_id } = usePricingKey()
  return useQuery({
    queryKey: productKeys.search(query, customer_id),
    queryFn: async (): Promise<Product[]> => {
      // Param is `q` (BE handler name); previous `query` and `per_page`
      // were both ignored — `query` doesn't match any handler arg and
      // `per_page` is server-set via SysConfig.
      const params: Record<string, unknown> = { q: query }
      if (customer_id) params.customer_id = customer_id
      const data = await v2api.get<ProductsSearchResponse | Product[]>(v2('/products/search'), params)
      if (Array.isArray(data)) return data
      return data?.products ?? []
    },
    enabled: enabled && query.length >= 2,
    staleTime: 1000 * 10,
    networkMode: 'online',
  })
}


export function useProduct(productId: number | null) {
  return useQuery({
    queryKey: productKeys.detail(productId!),
    queryFn: async (): Promise<Product> => fetchProductById(productId!),
    enabled: !!productId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 60 * 24,
  })
}

async function fetchProductById(productId: number): Promise<Product> {
  return v2api.get<Product>(v2Unscoped(`/objects/products/${productId}`))
}

function isProductLike(value: unknown): value is Product {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    typeof (value as { id?: unknown }).id === 'number'
  )
}

/**
 * Look up a Product in the React Query cache without firing a request.
 * Walks the per-product detail cache first, then the list / infinite /
 * search caches that the catalog screens populate. Returns undefined
 * when the product hasn't been fetched in this session.
 *
 * Used by the recall flow to enrich receipt-line stubs (which only
 * carry product_id, description, price, is_cannabis) with catalog
 * fields like image_url, brand_name, test_thc, on_hand — so a recalled
 * cart row renders the same as a freshly added one.
 */
export function getCachedProduct(
  queryClient: QueryClient,
  productId: number,
): Product | undefined {
  const detail = queryClient.getQueryData<Product>(productKeys.detail(productId))
  if (detail && isProductLike(detail)) return detail

  const entries = queryClient.getQueriesData<unknown>({ queryKey: productKeys.all })
  for (const [, data] of entries) {
    if (!data) continue
    if (Array.isArray(data)) {
      const hit = data.find((p) => isProductLike(p) && p.id === productId)
      if (hit && isProductLike(hit)) return hit
      continue
    }
    if (typeof data === 'object' && 'pages' in (data as object)) {
      const pages = (data as { pages?: Array<{ items?: unknown[] }> }).pages
      if (!Array.isArray(pages)) continue
      for (const page of pages) {
        const items = page?.items
        if (!Array.isArray(items)) continue
        const hit = items.find((p) => isProductLike(p) && p.id === productId)
        if (hit && isProductLike(hit)) return hit
      }
    }
  }
  return undefined
}

/**
 * Build a productId → Product map for the given ids. Reads cached
 * entries first; for any misses, fetches `/objects/products/{id}` in
 * parallel and seeds the detail cache so subsequent lookups are free.
 * Failures (deleted SKU, network blip) are swallowed — the recall
 * flow falls back to the receipt-line stub for those ids.
 */
export async function loadProductsForRecall(
  queryClient: QueryClient,
  productIds: number[],
): Promise<Map<number, Product>> {
  const map = new Map<number, Product>()
  const missing: number[] = []
  for (const id of productIds) {
    const cached = getCachedProduct(queryClient, id)
    if (cached) {
      map.set(id, cached)
    } else {
      missing.push(id)
    }
  }
  if (missing.length === 0) return map

  await Promise.allSettled(
    missing.map(async (id) => {
      try {
        const product = await queryClient.fetchQuery({
          queryKey: productKeys.detail(id),
          queryFn: () => fetchProductById(id),
          staleTime: 1000 * 60 * 5,
        })
        if (product) map.set(id, product)
      } catch {
        // Leave this id out of the map — recall falls back to the stub.
      }
    }),
  )
  return map
}


interface CategoryGroup {
  id: number
  name: string
  image?: string | null
  type?: number
  typename?: string
  is_ingredient?: boolean
}


export function useCategories() {
  return useQuery({
    queryKey: productKeys.categories(),
    queryFn: async (): Promise<ProductCategory[]> => {
      // `in_stock: true` scopes the response to category groups that have
      // at least one product with positive on-hand inventory in the
      // cashier's current store. The BE applies the same availability
      // predicate the products-search endpoint uses, so the bar matches
      // what's actually sellable below.
      const data = await v2api.get<CategoryGroup[]>(
        v2Unscoped('/objects/category_groups'),
        { in_stock: true },
      )
      return (data || [])
        .filter((g: CategoryGroup) => !g.is_ingredient)
        .map((g: CategoryGroup) => ({
          id: g.id,
          name: g.name,
          parent_id: null,
          product_count: 0,
          icon: g.image || undefined,
        }))
    },
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60 * 24,
    networkMode: 'offlineFirst',
  })
}


// Top-sellers panel data. Backed by /v2/.../products/top-sellers, which
// aggregates SA_JournalTransaction over the lookback window for the
// current store and returns the top N by units sold. Response shape is
// identical to /products/search (Product[] with full tier pricing,
// discount schedules, on-hand, image thumbs, etc.) so the click→modal
// path stays unchanged. customer_id is passed through for tier pricing,
// matching what /products/search does — the sidebar's prices then
// reflect the selected customer's actual prices, not shelf.
// Cached for 1h because the rolling 30-day aggregate barely shifts
// within a shift.
export function useTopSellers(limit = 50, days = 30) {
  const { customer_id } = usePricingKey()
  return useQuery({
    queryKey: productKeys.topSellers(days, limit, customer_id),
    queryFn: async (): Promise<Product[]> => {
      const params: Record<string, unknown> = { days, limit }
      if (customer_id) params.customer_id = customer_id
      const data = await v2api.get<ProductsSearchResponse | Product[]>(
        v2('/products/top-sellers'),
        params,
      )
      if (Array.isArray(data)) return data
      return data?.products ?? []
    },
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60 * 24,
    networkMode: 'offlineFirst',
  })
}


// GET /v2/.../products/by-barcode?code=<value>[&customer_id=<id>] —
// strict barcode lookup. Returns the matching Product on 200, null
// on 404 (no match, or matched product is deleted/inactive — the BE
// deliberately doesn't differentiate). 409 (ambiguous_barcode) and
// other errors propagate to React Query's error path.
//
// Replaces the legacy /products/search-with-strict-match dance: the
// old call relied on /products/search?query=<code>&per_page=1 which
// fuzzy-matched and returned a fallback product on no match, forcing
// the FE to validate the response with productMatchesScannedCode.
// The new endpoint is authoritative on "code didn't match anything"
// and the FE-side guard becomes a soak-window regression alarm only.
export function useProductByBarcode(barcode: string | null) {
  const { customer_id } = usePricingKey()
  return useQuery({
    queryKey: productKeys.barcode(barcode!, customer_id),
    queryFn: async (): Promise<Product | null> => {
      const params: Record<string, unknown> = { code: barcode }
      if (customer_id) params.customer_id = customer_id
      try {
        return await v2api.get<Product>(v2('/products/by-barcode'), params)
      } catch (err) {
        // 404 is the normal "no such barcode" outcome — resolve to
        // null so the consuming hook stays in `success` state with
        // data:null (same contract callers had before). Anything
        // else (network blip, 5xx, 400 misuse, 409 ambiguous)
        // bubbles to React Query's error path.
        if (err instanceof V2ApiError && err.status === 404) return null
        throw err
      }
    },
    // BE's min length is 2; we keep the FE bound at 3 to match the
    // legacy gate and avoid firing on typo-burst cases.
    enabled: !!barcode && barcode.length >= 3,
    staleTime: 1000 * 10,
    networkMode: 'online',
  })
}


export interface Brand {
  id: number
  name: string
  description?: string | null
  image?: string | null
}


export function useBrands() {
  return useQuery({
    queryKey: productKeys.brands(),
    queryFn: async (): Promise<Brand[]> => {
      const data = await v2api.get<Brand[]>(v2Unscoped('/objects/brands'))
      return (data || [])
        .filter((b): b is Brand => !!b && typeof b.id === 'number' && !!b.name)
        .sort((a, b) => a.name.localeCompare(b.name))
    },
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60 * 24,
    networkMode: 'offlineFirst',
  })
}


export interface ProductType {
  id: number
  name: string
  is_deleted?: boolean
}


export function useTypes() {
  return useQuery({
    queryKey: productKeys.types(),
    queryFn: async (): Promise<ProductType[]> => {
      const data = await v2api.get<ProductType[]>(v2Unscoped('/objects/types'))
      return (data || [])
        .filter((t): t is ProductType => !!t && typeof t.id === 'number' && !!t.name && !t.is_deleted)
        .sort((a, b) => a.name.localeCompare(b.name))
    },
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60 * 24,
    networkMode: 'offlineFirst',
  })
}


export interface ProductUnit {
  id: number
  name: string
  is_deleted?: boolean
}


export function useUnits() {
  return useQuery({
    queryKey: productKeys.units(),
    queryFn: async (): Promise<ProductUnit[]> => {
      const data = await v2api.get<ProductUnit[]>(v2Unscoped('/objects/units'))
      // BE returns them ordered by group/order_flag/name; preserve that
      // server-side ordering rather than re-sorting alphabetically so
      // related units (mg, g, kg) stay grouped in the chip rail.
      return (data || []).filter(
        (u): u is ProductUnit => !!u && typeof u.id === 'number' && !!u.name && !u.is_deleted,
      )
    },
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60 * 24,
    networkMode: 'offlineFirst',
  })
}

/** True for the synthesized fallback keys produced when the BE returns
 *  empty or duplicate `batch_number` values. Optimistic-add callers use
 *  this to detect "no real lot pinned yet" without leaking the synthetic
 *  prefix into their own logic. */
export function isSyntheticLotKey(key: string | null | undefined): boolean {
  return typeof key === 'string' && key.startsWith('__nolot_')
}

/**
 * Fetch + normalize the batches list for a product. Single source of
 * truth for the `[product, batches, customer_id]` cache entry — both
 * `useProductBatches` (modal consumer) and the optimistic-add prefetch
 * in `/pos` route call this so the cache always holds `ProductLot[]`,
 * never the raw BE shape. (Mismatched shapes here cause the modal to
 * render with `lot_number === undefined` on first open: every row
 * collides on a single `batchQty[undefined]` and `selectedKey ===
 * undefined` matches every row, so the panel shows all batches as
 * selected with qty 1 until the next refetch.)
 */
export async function fetchProductBatches(
  productId: number,
  customerId: number | null = null,
): Promise<ProductLot[]> {
  const params: Record<string, unknown> = { product_id: productId }
  if (customerId) params.customer_id = customerId
  const data = await v2api.get<any[]>(v2Unscoped('/objects/products/batches'), params)

  const toN = (v: unknown): number | null => {
    if (v === null || v === undefined || v === '') return null
    const n = typeof v === 'number' ? v : parseFloat(String(v))
    return Number.isFinite(n) ? n : null
  }

  // The BE keeps an "empty batch" legacy row that's typically
  // returned with `quantity: 0`. Filter it out so it doesn't
  // surface as an unlabelled row in the BatchPanel and trip the
  // modal's seed fallback (`batches[0]`) into picking the wrong
  // lot. Negative quantities ARE kept on purpose — those signal
  // a stock-discrepancy condition the BE team needs to see and
  // correct, not something the FE should silently swallow.
  //
  // Uniqueness pass: the modal keys per-batch state (qty stepper,
  // selected highlight, React key) off `lot_number`. When the BE
  // returns `batch_number: null` on more than one row — which
  // happens for the legacy empty/summary rows — every such row
  // collides on `''` and `setBatchQty['']` flows into all of them
  // (every row shows qty=1, total=1). Synthesize a unique key
  // when the real value is empty or duplicated. The synthetic
  // marker is hidden in the BatchPanel label and never written
  // back to the cart line on submit.
  const seenLot = new Set<string>()
  const mapped = (data || [])
    .filter((batch: any) => batch && Number(batch.quantity) !== 0)
    .map((batch: any, index: number) => {
      const real = batch.batch_number ? String(batch.batch_number).trim() : ''
      let lotKey = real
      if (!lotKey || seenLot.has(lotKey)) {
        lotKey = `__nolot_${index}__`
      }
      seenLot.add(lotKey)
      return {
        id: lotKey,
        lot_number: lotKey,
        quantity: Number(batch.quantity) || 0,
        expiration_date: batch.package_date || null,
        cost: null,
        thc: batch.thc || null,
        cbd: batch.cbd || null,
        test_units: batch.test_units ?? null,
        price: toN(batch.price),
        discounted_price: toN(batch.discounted_price),
      }
    })
  // Batch list order in ProductModal:
  //   1) rows with no date first
  //   2) dated rows oldest -> newest
  // For invalid date strings, fall back to lexical comparison so
  // ordering stays deterministic.
  mapped.sort((a, b) => {
    const aRaw =
      typeof a.expiration_date === 'string' && a.expiration_date.trim()
        ? a.expiration_date.trim()
        : null
    const bRaw =
      typeof b.expiration_date === 'string' && b.expiration_date.trim()
        ? b.expiration_date.trim()
        : null

    if (!aRaw && !bRaw) return 0
    if (!aRaw) return -1
    if (!bRaw) return 1

    const aTs = Date.parse(aRaw)
    const bTs = Date.parse(bRaw)
    const aValid = Number.isFinite(aTs)
    const bValid = Number.isFinite(bTs)

    if (aValid && bValid && aTs !== bTs) {
      return aTs - bTs
    }

    const fallback = aRaw.localeCompare(bRaw)
    if (fallback !== 0) return fallback
    return a.lot_number.localeCompare(b.lot_number)
  })

  return mapped
}

// Fetch available batches/lots for a product. When a customer is selected
// we forward `customer_id` so the v2 wrapper at v2/objects.py resolves
// customer → group_id + price_field and the shared handler returns
// `discounted_price` per batch (the customer-tier-priced number that
// matches /cart/preview). Without it the modal-from-cart silently
// falls back to the catalog `price2` and disagrees with the cart line.
export function useProductBatches(productId: number | null, enabled = true) {
  const { customer_id } = usePricingKey()
  return useQuery({
    queryKey: [...productKeys.detail(productId!), 'batches', customer_id],
    queryFn: () => fetchProductBatches(productId!, customer_id),
    enabled: !!productId && enabled,
    staleTime: 1000 * 10, // 10 seconds - batches change frequently
    gcTime: 1000 * 60,
  })
}
